import { DATE_FORMATS, getLabResultShortName, type LabResultCategoryGroup } from '@liza/fhir-utils';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { formatVitalValueParts } from '../../utils/observation';
import { isAbnormalObservation } from './observationDisplayUtils';

/**
 * Every row a category lists, as `Measurements` expects them: its curated members in panel order,
 * measured or not. Passing them all is what makes the view a chart rather than a list of whatever
 * happens to have been collected — a member with no reading renders an empty row, and `Measurements`
 * snaps concept siblings onto the member code at the same time.
 */
export const labCategoryExpectedMeasurements = (
  category: LabResultCategoryGroup,
  displayForResultCode: ReturnType<typeof useCodeDisplay>['displayForResultCode'],
): { code: string; name: string }[] =>
  category.codes.map((code) => {
    const drawn = category.groups.find((group) => group.key === code);
    return { code, name: displayForResultCode(code, drawn?.latest.code) };
  });

/** Every reading in a category, across its rows. */
export const labCategoryObservations = (
  category: LabResultCategoryGroup,
): LabResultCategoryGroup['groups'][number]['records'] => category.groups.flatMap((group) => group.records);

export type LabCategorySummaryItem = {
  code: string;
  /** The chart abbreviation ("Cr"), or the display name for a lab that has none. */
  name: string;
  value: string;
  unit?: string;
  /** Flagged by the lab's interpretation or by falling outside its reference range. */
  abnormal: boolean;
  latest: WithId<Observation>;
};

export type LabCategorySummaryLine = {
  /** The newest reading's `effectiveDateTime` on this line; absent when the readings are undated. */
  date?: string;
  /** In panel order. */
  items: LabCategorySummaryItem[];
};

const localDayOf = (observation: Observation): string => {
  const at = observation.effectiveDateTime ?? observation.issued;
  return at ? format(parseISO(at), DATE_FORMATS.ISO_DATE) : '';
};

const epochOf = (at: string | undefined): number => (at ? parseISO(at).getTime() : Number.NEGATIVE_INFINITY);

/**
 * The value several qualitative results on a line all share ("GC/CT, RPR, HIV — all negative"): every
 * value identical and none of them a quantity. The fold is abnormal when any of the readings is, so a
 * panel that agrees on a bad answer stays flagged. `undefined` when the line does not fold.
 */
export const sharedTextValue = (line: LabCategorySummaryLine): { value: string; abnormal: boolean } | undefined => {
  if (line.items.length < 2) return undefined;
  const [first] = line.items;
  if (!first || first.unit || first.latest.valueQuantity) return undefined;
  const agree = line.items.every((item) => !item.unit && !item.latest.valueQuantity && item.value === first.value);
  return agree ? { value: first.value, abnormal: line.items.some((item) => item.abnormal) } : undefined;
};

/**
 * A collapsed category on as few lines as its draw dates allow: each lab's latest reading, labs drawn
 * the same day sharing a line, newest line first. A panel drawn together reads as one line ("Na 139,
 * K 4.4, Ca 10.2 · Feb 10"); a lab last checked years before the others gets its own, so the stale
 * date is visible rather than hidden behind the panel's.
 */
export const labCategorySummaryLines = (
  category: LabResultCategoryGroup,
  displayForResultCode: ReturnType<typeof useCodeDisplay>['displayForResultCode'],
  unknownLabel: string,
): LabCategorySummaryLine[] => {
  const byDay = new Map<string, LabCategorySummaryLine>();
  for (const code of category.codes) {
    const group = category.groups.find((g) => g.key === code);
    if (!group) continue;
    const { latest } = group;
    const { value, unit } = formatVitalValueParts(latest, unknownLabel);
    const item: LabCategorySummaryItem = {
      code,
      name: getLabResultShortName(code) ?? displayForResultCode(code, latest.code),
      value,
      unit,
      abnormal: isAbnormalObservation(latest),
      latest,
    };
    const day = localDayOf(latest);
    const line = byDay.get(day);
    if (!line) {
      byDay.set(day, { date: latest.effectiveDateTime ?? latest.issued, items: [item] });
    } else {
      line.items.push(item);
      if (epochOf(latest.effectiveDateTime ?? latest.issued) > epochOf(line.date)) {
        line.date = latest.effectiveDateTime ?? latest.issued;
      }
    }
  }
  return [...byDay.values()].sort((a, b) => epochOf(b.date) - epochOf(a.date));
};
